import React, { useState } from 'react';
import { Button, Checkbox, Collapse, Empty, Pagination, Spin } from 'antd';
import type { TableProps } from 'antd';
import type { ColumnType, ColumnsType } from 'antd/es/table';

const PREFERRED = ['运单号', '状态', '客户', '渠道', '总费用', '总价', '时效', '名称', '批次', '代理'];

function flattenColumns<T>(cols: ColumnsType<T> | undefined): ColumnType<T>[] {
  const out: ColumnType<T>[] = [];
  (cols || []).forEach((col) => {
    if ('children' in col && col.children) {
      out.push(...flattenColumns(col.children as ColumnsType<T>));
    } else {
      out.push(col as ColumnType<T>);
    }
  });
  return out;
}

function isActionCol<T>(col: ColumnType<T>): boolean {
  const title = typeof col.title === 'string' ? col.title : '';
  const key = String(col.key ?? '');
  return title === '操作' || key === 'action' || key === 'actions' || key === 'op';
}

function titleText<T>(col: ColumnType<T>): string {
  if (typeof col.title === 'string' && col.title) return col.title;
  if (col.key != null) return String(col.key);
  if (typeof col.dataIndex === 'string') return col.dataIndex;
  return '字段';
}

function readValue(record: unknown, dataIndex: ColumnType<unknown>['dataIndex']): unknown {
  if (dataIndex == null || record == null) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  return path.reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[String(key)];
  }, record);
}

function renderCell<T>(col: ColumnType<T>, record: T, index: number): React.ReactNode {
  const value = readValue(record, col.dataIndex as ColumnType<unknown>['dataIndex']);
  if (col.render) {
    const node = col.render(value as never, record, index);
    if (node && typeof node === 'object' && 'children' in (node as object) && 'props' in (node as object)) {
      return (node as { children?: React.ReactNode }).children ?? null;
    }
    return node as React.ReactNode;
  }
  if (value == null || value === '') return '-';
  if (typeof value === 'object') return null;
  return String(value);
}

function rowKeyOf<T>(rowKey: TableProps<T>['rowKey'], record: T, index: number): React.Key {
  if (typeof rowKey === 'function') return rowKey(record, index);
  if (typeof rowKey === 'string') {
    const value = (record as Record<string, unknown>)[rowKey];
    if (value != null && value !== '') return value as React.Key;
  }
  return index;
}

function RecordCards<T extends object>({
  columns,
  dataSource,
  rowKey,
  loading,
  pagination,
  onRow,
  rowSelection,
  expandable,
  locale,
}: TableProps<T>) {
  const data = (dataSource || []) as T[];
  const flat = flattenColumns(columns);
  const actionCols = flat.filter(isActionCol);
  const fieldCols = flat.filter((col) => !isActionCol(col));
  const preferred = fieldCols.filter((col) => PREFERRED.some((name) => titleText(col).includes(name)));
  const primary = (preferred.length >= 2 ? [...preferred] : [...fieldCols]).slice(0, 4);
  for (const col of fieldCols) {
    if (primary.length >= 4) break;
    if (!primary.includes(col)) primary.push(col);
  }
  const primaryKeys = new Set(primary.map((col) => col.key ?? col.dataIndex ?? col.title));
  const rest = fieldCols.filter((col) => !primaryKeys.has(col.key ?? col.dataIndex ?? col.title));

  const pag = pagination ? pagination : undefined;
  const [innerPage, setInnerPage] = useState(1);
  const [innerSize, setInnerSize] = useState<number>(pag?.pageSize || pag?.defaultPageSize || 10);
  const page = pag?.current ?? innerPage;
  const size = pag?.pageSize || innerSize;
  const alreadyPaged = pag?.total != null && data.length < pag.total;
  const view = !pag || alreadyPaged ? data : data.slice((page - 1) * size, page * size);
  const total = pag ? (pag.total ?? data.length) : data.length;

  const selected = new Set((rowSelection?.selectedRowKeys || []).map(String));

  const toggleKey = (key: React.Key, record: T, checked: boolean) => {
    if (!rowSelection?.onChange) return;
    const prev = rowSelection.selectedRowKeys || [];
    const nextKeys = checked ? [...prev, key] : prev.filter((item) => String(item) !== String(key));
    const byKey = new Map<string, T>();
    data.forEach((row, index) => byKey.set(String(rowKeyOf(rowKey, row, index)), row));
    byKey.set(String(key), record);
    const nextRows = nextKeys.map((item) => byKey.get(String(item))).filter((row): row is T => !!row);
    rowSelection.onChange(nextKeys, nextRows, { type: 'single' });
  };

  return (
    <Spin spinning={!!loading}>
      {view.length === 0 ? (
        <Empty description={typeof locale?.emptyText === 'function' ? locale.emptyText() : (locale?.emptyText || '暂无数据')} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {view.map((record, index) => {
            const absoluteIndex = !pag || alreadyPaged ? index : (page - 1) * size + index;
            const key = rowKeyOf(rowKey, record, absoluteIndex);
            const rowProps = onRow?.(record, absoluteIndex);
            const disabled = rowSelection?.getCheckboxProps?.(record)?.disabled;
            return (
              <div
                key={String(key)}
                className="kfc-record-card"
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('button, a, input, .ant-checkbox, .ant-dropdown, .ant-collapse')) return;
                  rowProps?.onClick?.(e as unknown as React.MouseEvent<HTMLElement>);
                }}
                style={{
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: 12,
                  cursor: rowProps?.onClick ? 'pointer' : undefined,
                  ...(rowProps?.style || {}),
                }}
              >
                {rowSelection && (
                  <Checkbox
                    style={{ marginBottom: 8, minHeight: 44, display: 'inline-flex', alignItems: 'center' }}
                    checked={selected.has(String(key))}
                    disabled={disabled}
                    onChange={(e) => toggleKey(key, record, e.target.checked)}
                  />
                )}
                {primary.map((col) => (
                  <div key={String(col.key ?? titleText(col))} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'flex-start' }}>
                    <span style={{ color: '#888', flex: '0 0 72px' }}>{titleText(col)}</span>
                    <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{renderCell(col, record, absoluteIndex)}</span>
                  </div>
                ))}
                {rest.length > 0 && (
                  <Collapse
                    ghost
                    size="small"
                    items={[{
                      key: 'more',
                      label: '更多字段',
                      children: rest.map((col) => (
                        <div key={String(col.key ?? titleText(col))} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                          <span style={{ color: '#888', flex: '0 0 88px' }}>{titleText(col)}</span>
                          <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{renderCell(col, record, absoluteIndex)}</span>
                        </div>
                      )),
                    }]}
                  />
                )}
                {expandable?.expandedRowRender && (
                  <Collapse
                    ghost
                    size="small"
                    items={[{
                      key: 'expand',
                      label: '展开',
                      children: expandable.expandedRowRender(record, absoluteIndex, absoluteIndex, false),
                    }]}
                  />
                )}
                {actionCols.length > 0 && (
                  <div className="kfc-record-actions">
                    {actionCols.map((col) => (
                      <React.Fragment key={String(col.key ?? 'action')}>
                        {renderCell(col, record, absoluteIndex)}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {pag && total > 0 && (
        <Pagination
          style={{ marginTop: 12, textAlign: 'right' }}
          current={page}
          pageSize={size}
          total={total}
          showSizeChanger={pag.showSizeChanger}
          pageSizeOptions={pag.pageSizeOptions}
          showTotal={pag.showTotal}
          onChange={(nextPage, nextSize) => {
            if (pag.current == null) {
              setInnerPage(nextSize !== size ? 1 : nextPage);
              setInnerSize(nextSize);
            }
            pag.onChange?.(nextPage, nextSize);
          }}
        />
      )}
    </Spin>
  );
}

export default RecordCards;
